/**
 * The Builds board (Layers Management → Builds): every layer build across
 * all games on one page — who is running what (each runner: the always-on
 * worker, a Cloud Run job, a local build), how far each batch has got and
 * roughly how long it has left, what is queued behind, and what finished
 * last. One read (GET /api/v1/opsqa/builds/board) every 5 s while the tab
 * is visible. Cancel uses the per-batch cancel the match view uses; the
 * server enforces who may.
 *
 * Start a build: Rebuild L3+ (forced) for any game green through L2 —
 * POST .../rebuild-from-l3, which re-checks the chips and the
 * environment. Offered only where builds may overwrite (dev, local).
 */
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { Elapsed, secs, type BuildRun, type RunStatus, type StepStat } from './buildRuns'
import { greenThroughL2, relativeTime, type MatchRow } from './layersLabModel'
import { BuildWorkers } from './BuildWorkers'
import { SuggestedBuilds } from './SuggestedBuilds'

export type BoardRun = BuildRun & {
  game_id: number
  execution: string | null
}

type BoardPayload = {
  runs: BoardRun[]
  /** game id -> game code */
  games: Record<string, string>
  stats: StepStat[]
}

export type Batch = {
  batch_id: string
  game_id: number
  runs: BoardRun[]
  /** queued / running / cancelling anywhere in it */
  active: boolean
  /** the earliest-started running step (null when none) */
  running: BoardRun | null
  /** every step running now: a batch runs its ready steps side by side */
  runningAll: BoardRun[]
  execution: string | null
  runner: string | null
  done: number
  queued_at: string
  /** when its first step started: the whole run's clock (null before) */
  started_at: string | null
  finished_at: string | null
  /** worst outcome of a finished batch */
  outcome: 'succeeded' | 'failed' | 'cancelled' | null
}

const ACTIVE: RunStatus[] = ['queued', 'running', 'cancelling']
const FINISHED_OK: RunStatus[] = ['succeeded']
const POLL_MS = 5000
const SUGGEST_EVERY = 6

const STATUS_COLOR: Record<RunStatus, string> = {
  queued: '#6b7280', running: '#eab308', cancelling: '#f97316',
  succeeded: '#22c55e', failed: '#ef4444', skipped: '#9ca3af',
  cancelled: '#9ca3af',
}

/** Rows grouped into batches, in queue order (first run id). */
export function toBatches(runs: BoardRun[]): Batch[] {
  const by = new Map<string, BoardRun[]>()
  for (const r of [...runs].sort((a, b) => a.run_id - b.run_id)) {
    const list = by.get(r.batch_id) ?? []
    list.push(r)
    by.set(r.batch_id, list)
  }
  return [...by.entries()].map(([batch_id, rs]) => {
    rs.sort((a, b) => a.position - b.position)
    const active = rs.some((r) => ACTIVE.includes(r.status))
    const runningAll = rs.filter((r) => r.status === 'running' || r.status === 'cancelling')
    const running = runningAll[0] ?? null
    const finished = rs.map((r) => r.finished_at).filter((f): f is string => f != null).sort()
    const started = rs.map((r) => r.started_at).filter((f): f is string => f != null).sort()
    const outcome = active ? null
      : rs.some((r) => r.status === 'failed') ? 'failed'
        : rs.every((r) => r.status === 'succeeded') ? 'succeeded' : 'cancelled'
    const first = rs[0]!
    return {
      batch_id, game_id: first.game_id, runs: rs, active, running, runningAll,
      execution: rs.find((r) => r.execution)?.execution ?? null,
      runner: first.runner,
      done: rs.filter((r) => FINISHED_OK.includes(r.status)).length,
      queued_at: first.queued_at,
      started_at: started[0] ?? null,
      finished_at: active ? null : finished[finished.length - 1] ?? null,
      outcome,
    }
  })
}

export type FinishedGroup = {
  game_id: number
  /** every finished batch for the game, latest first */
  batches: Batch[]
  /** the latest finished batch: the row shown collapsed */
  latest: Batch
}

/** Finished batches grouped by game, newest run first in each group and
 *  groups ordered by their latest run — so one row stands for a game and
 *  expanding it shows that game's other runs. */
export function toFinishedGroups(finished: Batch[]): FinishedGroup[] {
  const by = new Map<number, Batch[]>()
  for (const b of finished) {
    const list = by.get(b.game_id) ?? []
    list.push(b)
    by.set(b.game_id, list)
  }
  const groups = [...by.entries()].map(([game_id, bs]) => {
    bs.sort((a, b) => (b.finished_at ?? '').localeCompare(a.finished_at ?? ''))
    return { game_id, batches: bs, latest: bs[0]! }
  })
  groups.sort((a, b) =>
    (b.latest.finished_at ?? '').localeCompare(a.latest.finished_at ?? ''))
  return groups
}

/** Which runner holds a batch, in words: the always-on worker (its host),
 *  a Cloud Run job execution, a local build, or none yet. */
export function runnerLabel(b: Pick<Batch, 'execution' | 'runner'>): string {
  const e = b.execution ?? ''
  if (e === 'worker:queued') return 'waiting for the build worker'
  if (e.startsWith('worker:')) return `worker ${e.split(':')[1] ?? ''}`.trim()
  if (e.includes('/executions/')) return `Cloud Run job ${e.split('/').pop()}`
  if (b.runner === 'local') return 'local'
  return e ? e : 'not dispatched yet'
}

/** Steps a batch runs at once — scripts/run_layer_build.py's
 *  LAYERSTACK_STEP_SLOTS default. */
export const STEP_SLOTS = 3

/** Milliseconds a batch probably has left: the median build time of each
 *  step still to run (less a running step's time so far), shared across
 *  STEP_SLOTS — but never less than the longest single step left; null
 *  when no step has history. */
export function estimateLeftMs(b: Batch, stats: Map<string, number>,
                               now: number = Date.now()): number | null {
  let work = 0
  let longest = 0
  let known = false
  for (const r of b.runs) {
    const m = stats.get(r.step)
    let left: number | null = null
    if (r.status === 'queued' && m != null) left = m
    if ((r.status === 'running' || r.status === 'cancelling') && m != null) {
      const ran = r.started_at ? now - new Date(r.started_at).getTime() : 0
      left = Math.max(0, m - ran)
    }
    if (left != null) {
      work += left
      longest = Math.max(longest, left)
      known = true
    }
  }
  return known ? Math.max(longest, work / STEP_SLOTS) : null
}

/** Median per step, from the runner with the most runs. */
function medians(stats: StepStat[]): Map<string, number> {
  const best = new Map<string, StepStat>()
  for (const s of stats) {
    const cur = best.get(s.step)
    if (!cur || s.n > cur.n) best.set(s.step, s)
  }
  return new Map([...best.entries()].map(([k, s]) => [k, s.median_ms]))
}

function StepChips({ runs }: { runs: BoardRun[] }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '3px' }}>
      {runs.map((r) => (
        <span key={r.run_id} title={`${r.step}: ${r.status}${r.error ? ` — ${r.error}` : ''}`}
              style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
                       color: '#fff', background: STATUS_COLOR[r.status] }}>
          {r.step}
        </span>
      ))}
    </span>
  )
}

function Progress({ b }: { b: Batch }) {
  const pct = b.runs.length ? (b.done / b.runs.length) * 100 : 0
  return (
    <div role="progressbar" aria-valuenow={b.done} aria-valuemin={0}
         aria-valuemax={b.runs.length}
         aria-label={`${b.done} of ${b.runs.length} steps`}
         style={{ height: '6px', borderRadius: '3px', background: '#333',
                  overflow: 'hidden', margin: '4px 0' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: '#22c55e' }} />
    </div>
  )
}

type MatchesPayload = { matches: MatchRow[]; overwrite_allowed?: boolean }

/** Pick a game green through L2 and queue Rebuild L3+ (force) for it. The
 *  index's rows are read once: the board's poll shows what follows. */
function StartRebuild({ onQueued }: { onQueued: () => void }) {
  const [rows, setRows] = useState<MatchRow[] | null>(null)
  const [allowed, setAllowed] = useState(false)
  const [gameId, setGameId] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    let alive = true
    api<MatchesPayload>('/api/v1/opsqa/matches')
      .then((d) => {
        if (!alive) return
        setRows(d.matches.filter((r) => !r.deleted))
        setAllowed(d.overwrite_allowed === true)
      })
      .catch(() => { if (alive) setRows([]) })
    return () => { alive = false }
  }, [])

  if (rows === null) return <p className="muted">Loading games…</p>
  if (!allowed) {
    return (
      <p className="muted" data-testid="start-rebuild">
        Forced rebuilds are off in this environment (overwriting built
        layers is not allowed here).
      </p>
    )
  }
  const eligible = rows.filter(greenThroughL2)
    .sort((a, b) => (b.match_date ?? '').localeCompare(a.match_date ?? '')
      || b.game_id - a.game_id)
  const start = () => {
    const id = Number(gameId)
    if (!id) return
    if (!confirm(`Force-rebuild L3 and everything above it for game #${id}? `
      + 'Built layers are overwritten.')) return
    setBusy(true); setMsg(null)
    api<{ batch_id: string }>(`/api/v1/opsqa/games/${id}/rebuild-from-l3`,
      { method: 'POST' })
      .then((d) => {
        setMsg({ ok: true,
                 text: `Queued Rebuild L3+ for #${id} (${d.batch_id.slice(0, 8)})` })
        setGameId('')
        onQueued()
      })
      .catch((e: unknown) => setMsg({
        ok: false, text: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false))
  }
  return (
    <div data-testid="start-rebuild"
         style={{ display: 'flex', gap: '8px', alignItems: 'center',
                  flexWrap: 'wrap', fontSize: '12px' }}>
      <label>
        Rebuild L3+ (force){' '}
        <select className="adm-select" value={gameId} aria-label="Game to rebuild"
                onChange={(e) => setGameId(e.target.value)}>
          <option value="">
            {eligible.length ? 'Choose a game green through L2' : 'No game is green through L2'}
          </option>
          {eligible.map((r) => (
            <option key={r.game_id} value={r.game_id}>
              #{r.game_id} {r.game_code ?? ''}
              {r.club?.name ? ` · ${r.club.name}` : ''}
              {r.match_date ? ` · ${r.match_date}` : ''}
            </option>
          ))}
        </select>
      </label>
      <button className="adm-btn" style={{ fontSize: '11px', padding: '2px 10px' }}
              disabled={!gameId || busy} onClick={start}>
        {busy ? 'Queueing…' : 'Queue rebuild'}
      </button>
      <span className="muted">
        {rows.length - eligible.length} game{rows.length - eligible.length === 1 ? '' : 's'} not
        green through L2 left out
      </span>
      {msg && <span style={{ color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
    </div>
  )
}

export function BuildBoard({ onOpen, canSetWorkers = false }: {
  onOpen: (gameId: number) => void
  /** Platform admin: may change the worker cap (the server enforces it). */
  canSetWorkers?: boolean
}) {
  const [data, setData] = useState<BoardPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  // Suggested reads again every SUGGEST_EVERY board polls (30 s), not 5 s:
  // its query scans every game, the board's does not
  const [polls, setPolls] = useState(0)
  // Games whose older finished runs are expanded in "Recently finished".
  const [expandedGames, setExpandedGames] = useState<Set<number>>(new Set())
  const toggleGame = (id: number) => setExpandedGames((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const load = useCallback(() => {
    if (document.visibilityState === 'hidden') return
    setPolls((n) => n + 1)
    api<BoardPayload>('/api/v1/opsqa/builds/board')
      .then((d) => { setData(d); setError(null) })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  const batches = useMemo(() => toBatches(data?.runs ?? []), [data])
  const med = useMemo(() => medians(data?.stats ?? []), [data])

  const cancel = (b: Batch) => {
    if (!confirm(`Cancel this batch for game #${b.game_id}? A step stopped `
      + 'mid-build may leave partial output.')) return
    api<{ detail?: string }>(`/api/v1/opsqa/builds/${b.batch_id}/cancel`, { method: 'POST' })
      .then((d) => { setMsg(`Cancel sent${d.detail ? `: ${d.detail}` : ''}`); load() })
      .catch((e: unknown) => setMsg(e instanceof ApiError ? e.message : String(e)))
  }

  if (error && !data) return <p className="error">{error}</p>
  if (!data) return <p className="muted">Loading builds…</p>

  const game = (id: number) => (
    <a href={`/ops/layer-stack?view=match&game=${id}`}
       onClick={(e) => { e.preventDefault(); onOpen(id) }}>
      #{id} {data.games[String(id)] ?? ''}
    </a>
  )
  const running = batches.filter((b) => b.active && b.running)
  const queued = batches.filter((b) => b.active && !b.running)
  const finished = batches.filter((b) => !b.active)
    .sort((a, b) => (b.finished_at ?? '').localeCompare(a.finished_at ?? ''))
  const queuedLeft = queued.reduce((n, b) => n + (estimateLeftMs(b, med) ?? 0), 0)

  // Runners: every execution holding a running batch.
  const runners = new Map<string, Batch[]>()
  for (const b of running) {
    const k = runnerLabel(b)
    runners.set(k, [...(runners.get(k) ?? []), b])
  }

  const cancelBtn = (b: Batch) => (
    <button className="adm-btn" style={{ fontSize: '10px', padding: '1px 6px', color: '#ef4444' }}
            onClick={() => cancel(b)}>
      Cancel
    </button>
  )

  return (
    <div data-testid="build-board">
      <SuggestedBuilds onOpen={onOpen}
                       refreshKey={Math.floor(polls / SUGGEST_EVERY)} />
      <StartRebuild onQueued={load} />
      <div className="adm-bar" style={{ gap: 'var(--space-3)', fontSize: '12px' }}>
        <span><strong>{runners.size}</strong> runner{runners.size === 1 ? '' : 's'} busy</span>
        <span><strong>{running.length}</strong> running</span>
        <span>
          <strong>{queued.length}</strong> queued
          {queued.length > 0 && queuedLeft > 0 && ` (~${secs(queuedLeft)} of work)`}
        </span>
        <BuildWorkers canEdit={canSetWorkers}
                      refreshKey={Math.floor(polls / SUGGEST_EVERY)} />
        {error && <span className="error">{error} (showing the last answer)</span>}
        {msg && <span className="muted">{msg}</span>}
      </div>

      <h2 className="t-label" style={{ marginTop: 'var(--space-3)' }}>Running</h2>
      {runners.size === 0 && <p className="muted">Nothing is building.</p>}
      {[...runners.entries()].map(([label, bs]) => (
        <section key={label} data-testid="runner"
                 style={{ border: '1px solid var(--color-border, #333)', borderRadius: '4px',
                          padding: '6px 10px', marginBottom: 'var(--space-2)' }}>
          <div style={{ fontSize: '12px', fontWeight: 600 }}>{label}</div>
          {bs.map((b) => {
            const left = estimateLeftMs(b, med)
            return (
              <div key={b.batch_id} style={{ fontSize: '12px', marginTop: '4px' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {game(b.game_id)}
                  <span>
                    {b.running!.status === 'cancelling' ? 'cancelling' : 'running'}{' '}
                    {b.runningAll.map((r, i) => (
                      <span key={r.run_id}>
                        {i > 0 && ', '}
                        <strong>{r.step}</strong>
                        {r.started_at && <Elapsed since={r.started_at} />}
                      </span>
                    ))}
                  </span>
                  <span className="muted">
                    {b.done} of {b.runs.length} done
                    {b.started_at && <><Elapsed since={b.started_at} /> elapsed</>}
                    {left != null && ` · ~${secs(left)} left`}
                    {b.runs.some((r) => r.force) && ' · force'}
                  </span>
                  {cancelBtn(b)}
                </div>
                <Progress b={b} />
                <StepChips runs={b.runs} />
              </div>
            )
          })}
        </section>
      ))}

      <h2 className="t-label" style={{ marginTop: 'var(--space-3)' }}>Queued</h2>
      {queued.length === 0 ? <p className="muted">Nothing queued.</p> : (
        <table className="adm-table" data-testid="queue">
          <thead><tr><th>#</th><th>Game</th><th>Steps</th><th>Waiting for</th>
            <th>Queued</th><th>Est.</th><th /></tr></thead>
          <tbody>
            {queued.map((b, i) => {
              const est = estimateLeftMs(b, med)
              return (
                <tr key={b.batch_id}>
                  <td className="t-data">{i + 1}</td>
                  <td>{game(b.game_id)}</td>
                  <td><StepChips runs={b.runs} /></td>
                  <td className="muted">{runnerLabel(b)}</td>
                  <td className="muted">{relativeTime(b.queued_at)}</td>
                  <td className="t-data">{est != null ? secs(est) : '—'}</td>
                  <td>{cancelBtn(b)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <h2 className="t-label" style={{ marginTop: 'var(--space-3)' }}>Recently finished</h2>
      {finished.length === 0 ? <p className="muted">No finished builds yet.</p> : (
        <table className="adm-table" data-testid="finished">
          <thead><tr><th>Game</th><th>Outcome</th><th>Steps</th><th>Took</th>
            <th>Finished</th></tr></thead>
          <tbody>
            {toFinishedGroups(finished).map((g) => {
              const open = expandedGames.has(g.game_id)
              const lead = (b: Batch, sub: boolean): ReactNode => {
                if (sub) return <span style={{ paddingLeft: '22px' }}>{game(b.game_id)}</span>
                if (g.batches.length === 1) return game(b.game_id)
                return (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <button className="adm-btn" aria-expanded={open}
                            aria-label={`${open ? 'Hide' : 'Show'} all runs for game #${g.game_id}`}
                            onClick={() => toggleGame(g.game_id)}
                            style={{ fontSize: '10px', padding: '0 5px', lineHeight: 1.6 }}>
                      {open ? '▾' : '▸'}
                    </button>
                    {game(b.game_id)}
                    <span className="muted" style={{ fontSize: '11px' }}>
                      · {g.batches.length} runs
                    </span>
                  </span>
                )
              }
              const row = (b: Batch, sub: boolean) => {
                const took = b.runs.reduce((n, r) => n + (r.duration_ms ?? 0), 0)
                const failed = b.runs.find((r) => r.status === 'failed')
                return (
                  <tr key={b.batch_id} data-testid={sub ? 'finished-run' : undefined}>
                    <td>{lead(b, sub)}</td>
                    <td style={{ color: STATUS_COLOR[b.outcome ?? 'cancelled'] }}
                        title={failed?.error ?? undefined}>
                      {b.outcome}{failed ? `: ${failed.step}` : ''}
                    </td>
                    <td><StepChips runs={b.runs} /></td>
                    <td className="t-data">{secs(took)}</td>
                    <td className="muted">{relativeTime(b.finished_at)}</td>
                  </tr>
                )
              }
              return (
                <Fragment key={g.game_id}>
                  {row(g.latest, false)}
                  {open && g.batches.slice(1).map((b) => row(b, true))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
